import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import GameOptions from "./GameOptions";
import { Bot } from "lucide-react";
import { useGameBot } from "./useGameBot";
import PlayerNameInput from "./PlayerNameInput";

const DotsAndBoxesGame = ({ onBack }) => {
  const navigate = useNavigate();
  
  // Configuration
  const [gridSize, setGridSize] = useState({ rows: 3, cols: 3 }); // 3x3 boxes = 4x4 dots

  // Game State
  // Lines: Matrices of integers (0 = none, 1 = P1, 2 = P2).
  // hLines[row][col] is line above box[row][col]. Matrix size: (rows+1) x cols
  // vLines[row][col] is line left of box[row][col]. Matrix size: rows x (cols+1)
  const [hLines, setHLines] = useState([]);
  const [vLines, setVLines] = useState([]);
  const [boxes, setBoxes] = useState([]); // Owner of box: 0 (none), 1, 2
  const [scores, setScores] = useState({ 1: 0, 2: 0 });
  
  const [gameStarted, setGameStarted] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [moveCount, setMoveCount] = useState(0);

  // Settings
  const [gameMode, setGameMode] = useState("PvP");
  const [difficulty, setDifficulty] = useState("Easy");
  const [player1Name, setPlayer1Name] = useState("Player 1");
  const [player2Name, setPlayer2Name] = useState("Player 2");

  const startGame = () => {
    // Initialize grids
    // rows=3 means 3 boxes high. Dots rows = 4.
    // Horizontal lines: (rows + 1) lines high, (cols) lines wide.
    const h = Array(gridSize.rows + 1).fill(null).map(() => Array(gridSize.cols).fill(0));
    
    // Vertical lines: (rows) lines high, (cols + 1) lines wide.
    const v = Array(gridSize.rows).fill(null).map(() => Array(gridSize.cols + 1).fill(0));
    
    const b = Array(gridSize.rows).fill(null).map(() => Array(gridSize.cols).fill(0));

    setHLines(h);
    setVLines(v);
    setBoxes(b);
    setScores({ 1: 0, 2: 0 });
    setGameStarted(true);
    setCurrentPlayer(1);
    setGameOver(false);
    setWinner(null);
    setMoveCount(0);
  };
  const getPlayerName = (id) => {
    if (gameMode === "PvC" && id === 2) return "Computer";
    return id === 1 ? player1Name : player2Name;
  };

  const checkGameOver = (currentBoxes) => {
      // Game over if all boxes are owned
      let filledCount = 0;
      let total = gridSize.rows * gridSize.cols;
      
      currentBoxes.forEach(row => {
          row.forEach(owner => {
              if (owner !== 0) filledCount++;
          });
      });

      if (filledCount === total) {
          return true;
      }
      return false;
  };

  // Move Logic
  // type: 'h' or 'v'
  // r, c: coordinates in the respective line matrix
  const handleMove = useCallback((type, r, c) => {
      // Must return next state for simulation or direct update
      return (currentHLines, currentVLines, currentBoxes, currentScores, player) => {
           let newHLines = currentHLines.map(row => [...row]);
           let newVLines = currentVLines.map(row => [...row]);
           let newBoxes = currentBoxes.map(row => [...row]);
           let newScores = { ...currentScores };
           let boxCompleted = false;

           if (type === 'h') {
               if (newHLines[r][c] !== 0) return null; // Already taken
               newHLines[r][c] = player;
           } else {
               if (newVLines[r][c] !== 0) return null; // Already taken
               newVLines[r][c] = player;
           }

           // Check for completed boxes
           // A horizontal line at r, c borders box(r-1, c) [above] and box(r, c) [below]
           // A vertical line at r, c borders box(r, c-1) [left] and box(r, c) [right]
           
           // Helper to check a specific box
           const isBoxComplete = (br, bc) => {
               if (br < 0 || br >= gridSize.rows || bc < 0 || bc >= gridSize.cols) return false;
               // Box needs: Top (h[br][bc]), Bottom (h[br+1][bc]), Left (v[br][bc]), Right (v[br][bc+1])
               return newHLines[br][bc] !== 0 && newHLines[br+1][bc] !== 0 && newVLines[br][bc] !== 0 && newVLines[br][bc+1] !== 0;
           };

           if (type === 'h') {
               if (isBoxComplete(r-1, c)) { // Check box above
                   if (newBoxes[r-1][c] === 0) {
                       newBoxes[r-1][c] = player;
                       newScores[player]++;
                       boxCompleted = true;
                   }
               }
               if (isBoxComplete(r, c)) { // Check box below
                   if (newBoxes[r][c] === 0) {
                       newBoxes[r][c] = player;
                       newScores[player]++;
                       boxCompleted = true;
                   }
               }
           } else {
                if (isBoxComplete(r, c-1)) { // Check box left
                   if (newBoxes[r][c-1] === 0) {
                       newBoxes[r][c-1] = player;
                       newScores[player]++;
                       boxCompleted = true;
                   }
               }
               if (isBoxComplete(r, c)) { // Check box right
                   if (newBoxes[r][c] === 0) {
                       newBoxes[r][c] = player;
                       newScores[player]++;
                       boxCompleted = true;
                   }
               }
           }

           return {
               newHLines,
               newVLines,
               newBoxes,
               newScores,
               nextTurn: boxCompleted ? player : (player === 1 ? 2 : 1),
               boxCompleted
           };
      };
  }, [gridSize]);

  const executeMove = (type, r, c) => {
      if (gameOver || !gameStarted) return;
      
      const logic = handleMove(type, r, c);
      const result = logic(hLines, vLines, boxes, scores, currentPlayer);
      
      if (!result) return; // Invalid move

      setHLines(result.newHLines);
      setVLines(result.newVLines);
      setBoxes(result.newBoxes);
      setScores(result.newScores);
      setMoveCount(prev => prev + 1);

      const isOver = checkGameOver(result.newBoxes);
      if (isOver) {
          setGameOver(true);
          const winnerId = result.newScores[1] > result.newScores[2] ? 1 : 
                           (result.newScores[2] > result.newScores[1] ? 2 : "Draw");
          setWinner(winnerId);
      } else {
          setCurrentPlayer(result.nextTurn);
      }
  };


  // Bot Logic
  const getBotMove = useCallback((diff, gameState) => {
      const { hLines, vLines, boxes, scores, currentPlayer } = gameState;
      
      const validMoves = [];
      // Collect valid H moves
      for(let r=0; r<hLines.length; r++) {
          for(let c=0; c<hLines[0].length; c++) {
              if (hLines[r][c] === 0) validMoves.push({ type: 'h', r, c });
          }
      }
      // Collect valid V moves
      for(let r=0; r<vLines.length; r++) {
          for(let c=0; c<vLines[0].length; c++) {
              if (vLines[r][c] === 0) validMoves.push({ type: 'v', r, c });
          }
      }

      if (validMoves.length === 0) return null;

      // Helper to count sides of a box
      const countSides = (br, bc, tempH, tempV) => {
           let count = 0;
           if (tempH[br][bc] !== 0) count++;
           if (tempH[br+1][bc] !== 0) count++;
           if (tempV[br][bc] !== 0) count++;
           if (tempV[br][bc+1] !== 0) count++;
           return count;
      };

      // 1. Look for moves that complete a box (Greedy)
      for (let move of validMoves) {
          // Simulate simple box completion check
          // If I place this line, does it close a box?
          // We can reuse logic or simple check
          let completes = false;
          if (move.type === 'h') {
               // Check above box (move.r-1, move.c) and below (move.r, move.c)
               if (move.r>0 && countSides(move.r-1, move.c, hLines, vLines) === 3) completes = true;
               if (move.r<gridSize.rows && countSides(move.r, move.c, hLines, vLines) === 3) completes = true;
          } else {
               // Check left box (move.r, move.c-1) and right (move.r, move.c)
               if (move.c>0 && countSides(move.r, move.c-1, hLines, vLines) === 3) completes = true;
               if (move.c<gridSize.cols && countSides(move.r, move.c, hLines, vLines) === 3) completes = true;
          }
          
          if (completes) return move;
      }

      // 2. If no box to complete, avoid giving a box to opponent (Don't make 3rd side)
      // Filter out moves that would set side count to 3
      if (diff !== "Easy") {
          const safeMoves = validMoves.filter(move => {
              let safe = true;
               if (move.type === 'h') {
                   // Check above box: if it currently has 2 sides, adding 1 makes 3 -> Dangerous
                   if (move.r>0 && countSides(move.r-1, move.c, hLines, vLines) === 2) safe = false;
                   if (move.r<gridSize.rows && countSides(move.r, move.c, hLines, vLines) === 2) safe = false;
               } else {
                   if (move.c>0 && countSides(move.r, move.c-1, hLines, vLines) === 2) safe = false;
                   if (move.c<gridSize.cols && countSides(move.r, move.c, hLines, vLines) === 2) safe = false;
               }
               return safe;
          });

          if (safeMoves.length > 0) {
               return safeMoves[Math.floor(Math.random() * safeMoves.length)];
          }
      }

      // 3. Random fall back
      return validMoves[Math.floor(Math.random() * validMoves.length)];

  }, [gridSize]);

  // Hook not working perfectly for complex state update injection, but we pass current state
  const { isBotThinking } = useGameBot({
    gameMode,
    makeMove: ({type, r, c}) => executeMove(type, r, c),
    playerTurn: currentPlayer,
    gameStarted,
    gameOver,
    botStrategy: getBotMove,
    gameState: { hLines, vLines, boxes, scores, currentPlayer },
    difficulty,
    trigger: moveCount
  });

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="mb-4">
        <button
          onClick={() => (onBack ? onBack() : navigate("/games"))}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Back to Games
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-xl p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Dots and Boxes</h1>
        <p className="text-center text-gray-600 mb-8">
             Connect dots to close boxes. Close a box to score a point and go again!
        </p>

        {!gameStarted ? (
           <div className="text-center">
             <GameOptions
               gameMode={gameMode}
               setGameMode={setGameMode}
               difficulty={difficulty}
               setDifficulty={setDifficulty}
               onStart={startGame}
             />
             <PlayerNameInput
                 player1Name={player1Name}
                 setPlayer1Name={setPlayer1Name}
                 player2Name={player2Name}
                 setPlayer2Name={setPlayer2Name}
                 gameMode={gameMode}
             />
             
             <div className="mb-6 max-w-lg mx-auto border-t pt-6">
                <label className="block text-lg font-medium text-gray-700 mb-2">Grid Size:</label>
                <div className="flex justify-center space-x-4">
                     {[3, 4, 5].map(size => (
                         <button
                            key={size}
                            onClick={() => setGridSize({ rows: size, cols: size })}
                            className={`px-4 py-2 rounded-lg border-2 ${
                                gridSize.rows === size && gridSize.cols === size && !gridSize.custom
                                    ? 'bg-blue-100 border-blue-500 text-blue-700' 
                                    : 'bg-white border-gray-300'
                            }`}
                         >
                             {size} x {size}
                         </button>
                     ))}
                     <button
                        onClick={() => setGridSize(prev => ({ ...prev, custom: true }))}
                        className={`px-4 py-2 rounded-lg border-2 ${
                            gridSize.custom ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-white border-gray-300'
                        }`}
                     >
                         Custom
                     </button>
                </div>
                
                {gridSize.custom && (
                    <div className="flex justify-center space-x-4 mt-4 items-center animate-fade-in">
                        <div className="flex items-center space-x-2">
                            <label className="text-gray-600 font-medium">Rows:</label>
                            <input 
                                type="number" 
                                min="2" 
                                max="10" 
                                value={gridSize.rows} 
                                onChange={(e) => {
                                    const val = Math.max(2, Math.min(10, parseInt(e.target.value) || 3));
                                    setGridSize(prev => ({ ...prev, rows: val }));
                                }}
                                className="w-16 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                        <span className="text-gray-400">×</span>
                        <div className="flex items-center space-x-2">
                            <label className="text-gray-600 font-medium">Cols:</label>
                            <input 
                                type="number" 
                                min="2" 
                                max="10" 
                                value={gridSize.cols} 
                                onChange={(e) => {
                                    const val = Math.max(2, Math.min(10, parseInt(e.target.value) || 3));
                                    setGridSize(prev => ({ ...prev, cols: val }));
                                }}
                                className="w-16 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                    </div>
                )}
             </div>

             <div className="mt-8 text-gray-600 max-w-2xl mx-auto">
               <h3 className="font-semibold mb-4 text-lg">Game Rules:</h3>
               <div className="bg-gray-50 p-4 rounded-lg text-left space-y-2">
                 <p>• <strong>Goal:</strong> Capture more boxes than your opponent.</p>
                 <p>• <strong>Turn:</strong> Click between two adjacent dots to draw a line.</p>
                 <p>• <strong>Scoring:</strong> If you draw the fourth line of a box, you capture it and get 1 point.</p>
                 <p>• <strong>Bonus Turn:</strong> Capturing a box grants you an immediate extra turn.</p>
               </div>
             </div>
           </div>
        ) : (
           <div>
               <div className="flex justify-between items-center mb-6 px-4 md:px-8">
                    {/* Player 1 Score Board */}
                    <div className={`
                        text-center px-6 py-4 rounded-xl transition-all duration-300 transform
                        ${currentPlayer === 1 ? 'bg-blue-100 shadow-md scale-105 ring-2 ring-blue-500' : 'bg-transparent opacity-80'}
                    `}>
                        <div className={`text-xl font-bold ${currentPlayer === 1 ? 'text-blue-700' : 'text-gray-500'}`}>
                             {getPlayerName(1)}
                        </div>
                        <div className="text-3xl font-bold text-blue-800">{scores[1]}</div>
                    </div>
                    
                    <div className="text-xl font-semibold text-gray-600 flex flex-col items-center">
                        {gameOver ? (
                             <span className="text-purple-600 text-2xl font-bold animate-bounce">
                                 {winner === "Draw" ? "It's a Draw!" : `${getPlayerName(winner)} Wins!`}
                             </span>
                        ) : (
                             <span className="text-gray-400">Vs</span>
                        )}
                        {isBotThinking && <div className="text-sm mt-1 text-purple-600 animate-pulse font-medium">Thinking...</div>}
                    </div>

                    {/* Player 2 Score Board */}
                    <div className={`
                        text-center px-6 py-4 rounded-xl transition-all duration-300 transform
                        ${currentPlayer === 2 ? 'bg-red-100 shadow-md scale-105 ring-2 ring-red-500' : 'bg-transparent opacity-80'}
                    `}>
                        <div className={`text-xl font-bold ${currentPlayer === 2 ? 'text-red-700' : 'text-gray-500'}`}>
                             {getPlayerName(2)}
                        </div>
                        <div className="text-3xl font-bold text-red-800">{scores[2]}</div>
                    </div>
               </div>

                <div className="flex justify-center mb-6">
                    <button onClick={() => setGameStarted(false)} className="px-4 py-2 bg-gray-200 text-gray-700 font-medium rounded hover:bg-gray-300 transition-colors">
                        New Game
                    </button>
                </div>

               <div className="flex justify-center select-none pb-8">
                    <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100" style={{ 
                        display: 'grid', 
                        gridTemplateColumns: `repeat(${gridSize.cols}, 20px 80px) 20px`,
                        gridTemplateRows: `repeat(${gridSize.rows}, 20px 80px) 20px` 
                    }}>
                        {/* 
                           Rendering strategy: 
                           Alternate Dot - HLine - Dot - HLine
                           VLine - Box - VLine - Box
                           ...
                        */}
                        
                        {Array.from({ length: 2 * gridSize.rows + 1 }).map((_, rIndex) => { // Render visual rows
                            // Even rIndex = Dot row (contains Dots and HLines)
                            // Odd rIndex = Box row (contains VLines and Boxes)
                            
                            const isDotRow = rIndex % 2 === 0;
                            const gridRow = Math.floor(rIndex / 2); // 0, 1, 2...

                            return Array.from({ length: 2 * gridSize.cols + 1 }).map((__, cIndex) => {
                                 const isDotCol = cIndex % 2 === 0;
                                 const gridCol = Math.floor(cIndex / 2);

                                 if (isDotRow) {
                                     if (isDotCol) {
                                         // DOT
                                         return <div key={`${rIndex}-${cIndex}`} className="w-5 h-5 bg-gray-800 rounded-full z-10" />;
                                     } else {
                                         // H-LINE
                                         // Located at hLines[gridRow][gridCol]
                                         const owner = hLines[gridRow][gridCol];
                                         const isActive = owner !== 0;
                                         return (
                                             <div 
                                                key={`${rIndex}-${cIndex}`} 
                                                className={`h-5 flex items-center justify-center cursor-pointer group relative`}
                                                onClick={() => !isActive && !gameOver && !isBotThinking && executeMove('h', gridRow, gridCol)}
                                             >
                                                 {/* Invisible hit area */}
                                                 <div className="absolute inset-0 -my-2 z-0" />
                                                 
                                                 <div className={`
                                                    h-2 w-full rounded transition-all duration-200 z-0
                                                    ${isActive 
                                                        ? (owner === 1 ? 'bg-blue-500' : 'bg-red-500') 
                                                        : 'bg-gray-200 group-hover:bg-yellow-200'}
                                                 `} />
                                             </div>
                                         );
                                     }
                                 } else {
                                     if (isDotCol) {
                                         // V-LINE
                                         // Located at vLines[gridRow][gridCol]
                                         const owner = vLines[gridRow][gridCol];
                                         const isActive = owner !== 0;
                                         return (
                                              <div 
                                                key={`${rIndex}-${cIndex}`} 
                                                className={`w-5 flex items-center justify-center cursor-pointer group relative`}
                                                onClick={() => !isActive && !gameOver && !isBotThinking && executeMove('v', gridRow, gridCol)}
                                             >
                                                 {/* Invisible hit area */}
                                                 <div className="absolute inset-0 -mx-2 z-0" />

                                                 <div className={`
                                                    w-2 h-full rounded transition-all duration-200 z-0
                                                    ${isActive 
                                                        ? (owner === 1 ? 'bg-blue-500' : 'bg-red-500') 
                                                        : 'bg-gray-200 group-hover:bg-yellow-200'}
                                                 `} />
                                             </div>
                                         );
                                     } else {
                                         // BOX
                                         // Located at boxes[gridRow][gridCol]
                                         const owner = boxes[gridRow][gridCol];
                                         return (
                                             <div key={`${rIndex}-${cIndex}`} className={`w-full h-full flex items-center justify-center rounded transition-all duration-500 transform
                                                ${owner === 1 ? 'bg-blue-100 scale-95' : (owner === 2 ? 'bg-red-100 scale-95' : '')}
                                             `}>
                                                 {owner === 1 && <span className="text-blue-500 font-bold text-2xl animate-pop-in">P1</span>}
                                                 {owner === 2 && (
                                                     gameMode === 'PvP' ? (
                                                        <span className="text-red-500 font-bold text-2xl animate-pop-in">P2</span>
                                                     ) : (
                                                        <Bot className="text-red-500 w-8 h-8 animate-pop-in" />
                                                     )
                                                 )}
                                             </div>
                                         );
                                     }
                                 }
                            });
                        })}
                    </div>
               </div>
           </div>
        )}
      </div>
    </div>
  );
};

export default DotsAndBoxesGame;
